import { useApi, useToaster } from "@shopexperts/shared";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref, toValue } from "vue";
import type {
  RegisterExpertSubscriptionInvoiceRequest,
  UpdateExpertSubscriptionInvoiceRequest,
} from "@shopexperts/legacy-api";
import type {
  AdminExpertInvoiceRecord,
  AdminExpertSubscriptionRecord,
  AdminSubscriptionManagement,
} from "./adminManagedSubscription";

export type SubscriptionLifecycleVariant = "default" | "highlight" | "success" | "warn";

export interface ExpertSubscriptionDisplayState {
  label: string;
  variant: SubscriptionLifecycleVariant;
  tone: "active" | "ending" | "expired" | "inactive";
  detail: string;
  sortOrder: number;
  isExpired: boolean;
  isRenewing: boolean;
}

export type ExpertSubscriptionWithDisplay = AdminExpertSubscriptionRecord & {
  displayState: ExpertSubscriptionDisplayState;
};

interface UpdateInvoiceMutationInput {
  subscriptionId: number;
  invoiceId: number;
  body: UpdateExpertSubscriptionInvoiceRequest;
}

interface DeleteInvoiceMutationInput {
  subscriptionId: number;
  invoiceId: number;
}

interface CancelSubscriptionMutationInput {
  subscriptionId: number;
}

function getTimeValue(dateStr: string | null | undefined) {
  if (!dateStr) return null;

  const time = new Date(dateStr).getTime();
  return Number.isNaN(time) ? null : time;
}

function formatShortDate(dateStr: string | null | undefined) {
  if (!dateStr) return "date unavailable";

  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getSubscriptionDisplayState(subscription: AdminExpertSubscriptionRecord): ExpertSubscriptionDisplayState {
  const status = subscription.status?.trim().toLowerCase() ?? "";
  const now = Date.now();
  const endsAt = getTimeValue(subscription.ends_at);
  const renewsAt = getTimeValue(subscription.renews_at);
  const cancelledAt = getTimeValue(subscription.cancelled_at);

  const hasExpired = endsAt !== null && endsAt < now;
  const hasUpcomingRenewal = renewsAt !== null && renewsAt >= now;
  const isActiveStatus = status === "active";

  if (hasExpired) {
    return {
      label: "Expired",
      variant: "highlight",
      tone: "expired",
      detail: `Expired ${formatShortDate(subscription.ends_at)}`,
      sortOrder: 3,
      isExpired: true,
      isRenewing: false,
    };
  }

  if (isActiveStatus && hasUpcomingRenewal) {
    return {
      label: "Active / Renewing",
      variant: "success",
      tone: "active",
      detail: `Renews ${formatShortDate(subscription.renews_at)}`,
      sortOrder: 0,
      isExpired: false,
      isRenewing: true,
    };
  }

  if (isActiveStatus && endsAt !== null && endsAt >= now) {
    return {
      label: "Scheduled to End",
      variant: "warn",
      tone: "ending",
      detail: `Ends ${formatShortDate(subscription.ends_at)}`,
      sortOrder: 1,
      isExpired: false,
      isRenewing: false,
    };
  }

  if (status === "cancelled") {
    const cancellationDetail = cancelledAt !== null
      ? `Cancelled ${formatShortDate(subscription.cancelled_at)}`
      : "Renewal stopped";

    return {
      label: "Cancelled",
      variant: "default",
      tone: "inactive",
      detail: endsAt !== null && endsAt >= now
        ? `${cancellationDetail} · Access until ${formatShortDate(subscription.ends_at)}`
        : endsAt !== null
          ? `Ended ${formatShortDate(subscription.ends_at)}`
          : cancellationDetail,
      sortOrder: 4,
      isExpired: false,
      isRenewing: false,
    };
  }

  if (status === "past_due") {
    return {
      label: "Past Due",
      variant: "warn",
      tone: "inactive",
      detail: renewsAt !== null ? `Renewal attempted ${formatShortDate(subscription.renews_at)}` : "Payment attention needed",
      sortOrder: 2,
      isExpired: false,
      isRenewing: false,
    };
  }

  const fallbackLabel = status
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");

  return {
    label: fallbackLabel || "Unknown",
    variant: "default",
    tone: "inactive",
    detail: endsAt !== null ? `Ends ${formatShortDate(subscription.ends_at)}` : "No lifecycle dates available",
    sortOrder: 5,
    isExpired: false,
    isRenewing: false,
  };
}

function compareSubscriptions(a: ExpertSubscriptionWithDisplay, b: ExpertSubscriptionWithDisplay) {
  if (a.displayState.sortOrder !== b.displayState.sortOrder) {
    return a.displayState.sortOrder - b.displayState.sortOrder;
  }

  const aRenewal = getTimeValue(a.renews_at) ?? Number.POSITIVE_INFINITY;
  const bRenewal = getTimeValue(b.renews_at) ?? Number.POSITIVE_INFINITY;
  if (aRenewal !== bRenewal) {
    return aRenewal - bRenewal;
  }

  const aEnd = getTimeValue(a.ends_at) ?? Number.POSITIVE_INFINITY;
  const bEnd = getTimeValue(b.ends_at) ?? Number.POSITIVE_INFINITY;
  if (aEnd !== bEnd) {
    return aEnd - bEnd;
  }

  return b.id - a.id;
}

interface UseExpertSubscriptionOptions {
  expertId: Ref<number> | number;
  management?: Ref<AdminSubscriptionManagement> | AdminSubscriptionManagement;
}

export function useExpertSubscription(options: UseExpertSubscriptionOptions) {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const expertIdValue = computed(() => toValue(options.expertId));
  const managementValue = computed(() => options.management ? toValue(options.management) : "all");
  const invalidateBillingQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["admin", "expert", expertIdValue.value, "subscriptions"],
    });
    queryClient.invalidateQueries({
      queryKey: ["admin", "expert", expertIdValue.value, "invoices"],
    });
  };

  const subscriptionsQuery = useQuery({
    queryKey: computed(() => [
      "admin", "expert", expertIdValue.value, "subscriptions", managementValue.value,
    ]),
    queryFn: async () => {
      return await api.admin.expertSubscriptions.listSubscriptions({
        params: { expert: expertIdValue.value },
        query: { invoice_limit: 3, management: managementValue.value },
      });
    },
    retry: 2,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const subscriptions = computed<ExpertSubscriptionWithDisplay[]>(() => {
    const data = subscriptionsQuery.data.value;
    const items = (Array.isArray(data) ? data : data ? [data] : []) as unknown as AdminExpertSubscriptionRecord[];

    return items
      .map((subscription) => ({
        ...subscription,
        displayState: getSubscriptionDisplayState(subscription),
      }))
      .sort(compareSubscriptions);
  });

  const activeSubscription = computed(() => {
    return subscriptions.value[0] ?? null;
  });

  const plansQuery = useQuery({
    queryKey: ["admin", "expert-plans"],
    queryFn: async () => {
      return await api.admin.expertSubscriptions.listPlans({
        query: { is_active: true },
      });
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const plans = computed(() => {
    const data = plansQuery.data.value;
    return data?.plans ?? [];
  });

  const defaultTermDays = computed(() => {
    return plansQuery.data.value?.default_term_days ?? null;
  });

  const registerInvoice = useMutation({
    mutationKey: ["admin", "expert", "registerInvoice"],
    mutationFn: async (body: RegisterExpertSubscriptionInvoiceRequest) => {
      return await api.admin.expertSubscriptions.registerInvoice({
        params: { expert: expertIdValue.value },
        body,
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Invoice registered successfully", type: "success" });
      invalidateBillingQueries();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to register invoice: ${error.message}`, type: "error" });
    },
  });

  const updateInvoice = useMutation({
    mutationKey: ["admin", "expert", "updateInvoice"],
    mutationFn: async ({ subscriptionId, invoiceId, body }: UpdateInvoiceMutationInput) => {
      return await api.admin.expertSubscriptions.updateInvoice({
        params: {
          expert: expertIdValue.value,
          subscription: subscriptionId,
          invoice: invoiceId,
        },
        body,
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Invoice updated successfully", type: "success" });
      invalidateBillingQueries();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to update invoice: ${error.message}`, type: "error" });
    },
  });

  const deleteInvoice = useMutation({
    mutationKey: ["admin", "expert", "deleteInvoice"],
    mutationFn: async ({ subscriptionId, invoiceId }: DeleteInvoiceMutationInput) => {
      return await api.admin.expertSubscriptions.deleteInvoice({
        params: {
          expert: expertIdValue.value,
          subscription: subscriptionId,
          invoice: invoiceId,
        },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Invoice deleted successfully", type: "success" });
      invalidateBillingQueries();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to delete invoice: ${error.message}`, type: "error" });
    },
  });

  const cancelSubscription = useMutation({
    mutationKey: ["admin", "expert", "cancelSubscription"],
    mutationFn: async ({ subscriptionId }: CancelSubscriptionMutationInput) => {
      return await api.admin.expertSubscriptions.cancelSubscription({
        params: {
          expert: expertIdValue.value,
          subscription: subscriptionId,
        },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Subscription cancelled successfully", type: "success" });
      invalidateBillingQueries();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to cancel subscription: ${error.message}`, type: "error" });
    },
  });

  return {
    subscriptions,
    activeSubscription,
    isLoadingSubscriptions: subscriptionsQuery.isLoading,
    plans,
    defaultTermDays,
    isLoadingPlans: plansQuery.isLoading,
    registerInvoice,
    isRegistering: registerInvoice.isPending,
    updateInvoice,
    isUpdatingInvoice: updateInvoice.isPending,
    deleteInvoice,
    isDeletingInvoice: deleteInvoice.isPending,
    cancelSubscription,
    isCancellingSubscription: cancelSubscription.isPending,
    refetchSubscriptions: subscriptionsQuery.refetch,
  };
}

interface UseExpertInvoicesOptions {
  expertId: Ref<number> | number;
  subscriptionId: Ref<number> | number;
}

export function useExpertInvoices(options: UseExpertInvoicesOptions) {
  const api = useApi();

  const expertIdValue = computed(() => toValue(options.expertId));
  const subscriptionIdValue = computed(() => toValue(options.subscriptionId));

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: computed(() => [
      "admin", "expert", expertIdValue.value, "invoices", subscriptionIdValue.value,
    ]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.expertSubscriptions.listInvoices({
        params: {
          expert: expertIdValue.value,
          subscription: subscriptionIdValue.value,
        },
        query: { limit: 20, cursor: pageParam },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: computed(() => !!subscriptionIdValue.value),
    retry: 2,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const invoices = computed<AdminExpertInvoiceRecord[]>(() => {
    return (data.value?.pages.flatMap((page) => page.data) || []) as unknown as AdminExpertInvoiceRecord[];
  });

  return {
    invoices,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}
